import type { WrappedStats } from './wrapped-model';
import { WrappedCharts } from './wrapped-charts';
import { awardFor, formatDecimal } from './wrapped-display-text';
import { WrappedAward, WrappedHero, WrappedStat } from './wrapped-primitives';

export function PersonalWrappedSection({ stats }: { stats: WrappedStats }) {
  return (
    <section className="wrapped-personal-section" aria-labelledby="wrapped-personal-title">
      <div className="wrapped-section-heading">
        <div>
          <p className="step-label">Teil eins · persönlich</p>
          <h2 id="wrapped-personal-title">Dein Jahr in Zahlen</h2>
          <p>Alle deine erfassten Getränke und Trinktage seit dem Start der App.</p>
        </div>
      </div>

      <WrappedHero stats={stats} personal />

      <section className="wrapped-stat-grid" aria-label="Persönliche Kennzahlen">
        <WrappedStat
          value={`${stats.drinkingDays} ${stats.drinkingDays === 1 ? 'Tag' : 'Tage'}`}
          label="Trinktage"
          hint="Tage mit mindestens einem Eintrag"
        />
        <WrappedStat
          value={formatDecimal(stats.averagePerDrinkingDay)}
          label="Getränke pro Trinktag"
          hint="Dein persönlicher Durchschnitt"
        />
        <WrappedStat
          value={`${stats.longestStreak} ${stats.longestStreak === 1 ? 'Tag' : 'Tage'}`}
          label="Längste Serie"
          hint="Tage in Folge mit Eintrag"
        />
        <WrappedStat
          value={`${stats.longestBreak} ${stats.longestBreak === 1 ? 'Tag' : 'Tage'}`}
          label="Längste Pause"
          hint="Zwischen zwei Trinktagen"
        />
        <WrappedStat
          value={stats.favoriteCategory ?? '—'}
          label="Lieblingsgetränk"
          hint="Häufigster erfasster Getränketyp"
        />
        <WrappedStat
          value={stats.strongestMonth?.label ?? '—'}
          label="Stärkster Monat"
          hint={`${stats.strongestMonth?.drinks ?? 0} Getränke`}
        />
        <WrappedStat
          value={stats.quietestMonth?.label ?? '—'}
          label="Ruhigster Monat"
          hint={`${stats.quietestMonth?.drinks ?? 0} Getränke`}
        />
        <WrappedStat
          value={stats.noDrinkDays.toString()}
          label="Tage ohne Eintrag"
          hint="Seit deinem ersten Eintrag bis heute"
        />
      </section>

      <WrappedCharts stats={stats} monthlyMetric="drinks" monthlyTitle="Getränke pro Monat" />

      <section
        className="wrapped-panel wrapped-panel--full"
        aria-labelledby="wrapped-personal-awards-title"
      >
        <div className="section-heading">
          <div>
            <p className="step-label">Deine Auszeichnungen</p>
            <h3 id="wrapped-personal-awards-title">Das passt zu dir</h3>
          </div>
        </div>
        <div className="wrapped-awards">
          <WrappedAward
            title={awardFor(stats, 'weekend')}
            description="Die meisten deiner Einträge lagen am Wochenende."
          />
          <WrappedAward
            title={awardFor(stats, 'category')}
            description={`Dein häufigster Typ war ${stats.favoriteCategory ?? 'nicht eindeutig'}.`}
          />
          <WrappedAward
            title={stats.longestStreak >= 3 ? 'Serienmensch' : 'Gelegenheitsgast'}
            description={`${stats.longestStreak} Tage war deine längste Folge von Trink-Tagen.`}
          />
        </div>
      </section>
    </section>
  );
}
